import dateFormat from "dateformat";
import { GraphQLError } from "graphql";
import React, { useEffect, useState } from "react";
import {
  addBabyMutation,
  addDiscountCodeMutation,
  addGiftCodeMutation,
  addObservationMutation,
  addOccupantMutation,
  addProductToBookingMutation,
  addProductToOccupantMutation,
  chooseProposalFromKeysMutation,
  confirmFastCheckoutMutation,
  removeBookingFromCartMutation,
  removeDiscountCodeMutation,
  removeGiftCodeMutation,
  removeOccupantMutation,
  removeStayMutation,
  resetCartMutation,
  saveCartMutation,
  switchToPartnerConventionMutation,
  updateCartPaymentMethodMutation,
  updateCartPaymentPlansMutation,
  updateCustomerBookingMutation,
  updateOccupantMutation,
  updatePartnerPaymentPlansMutation,
  updatePayerPaymentPlansMutation,
} from "rsl-api-106/lib/mutations";
import {
  getCartQuery,
  getCustomerBookingsQuery,
} from "rsl-api-106/lib/queries";
import {
  BookingStatus,
  Cart,
  ChooseProposalFromKeysOccupantInput,
  CustomerBooking,
  Mutation,
  MutationAddBabyArgs,
  MutationAddDiscountCodeArgs,
  MutationAddGiftCodeArgs,
  MutationAddObservationArgs,
  MutationAddOccupantArgs,
  MutationAddProductToBookingArgs,
  MutationAddProductToOccupantArgs,
  MutationChooseProposalFromKeysArgs,
  MutationConfirmFastCheckoutArgs,
  MutationRemoveBookingFromCartArgs,
  MutationRemoveDiscountCodeArgs,
  MutationRemoveGiftCodeArgs,
  MutationRemoveOccupantArgs,
  MutationRemoveStayArgs,
  MutationResetCartArgs,
  MutationSaveCartArgs,
  MutationSwitchToPartnerConventionArgs,
  MutationUpdateCartPaymentMethodArgs,
  MutationUpdateCartPaymentPlansArgs,
  MutationUpdateCustomerBookingArgs,
  MutationUpdateOccupantArgs,
  MutationUpdatePartnerPaymentPlansArgs,
  MutationUpdatePayerPaymentPlansArgs,
  Query,
  QueryGetCartArgs,
  QueryGetCustomerBookingsArgs,
} from "rsl-api-106/lib/types";

import { ApolloError, useApolloClient } from "@apollo/client";
import { CartContext } from "@contexts";
import { useSession } from "@hooks";
import { resetAndChooseProposalFromKeysMutation } from "@services/graphql2/querymutations";
import { deleteCachedProducts } from "@utils/cache";
import {
  removeLocalStorageItem,
  setLocalStorageItem,
} from "@utils/localStorage";
import { sendDataLayerAddToCart } from "@utils/tracking/gtm";
import isEmpty from "is-empty";

export const CartProvider: React.FC = ({ children }) => {
  /* Hooks */
  const client = useApolloClient();
  const { sessionId, funnel_type } = useSession();

  /* State */
  const [cart, setCart] = useState<Cart | null>(null);
  const [customerBooking, setCustomerBooking] =
    useState<CustomerBooking | null>(null);

  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (sessionId) {
      refresh((err) => {
        setLoading(false);
      });
    }
  }, [sessionId]);

  const addProposalFromKeys = async (
    proposalKeys: string[],
    occupantList: ChooseProposalFromKeysOccupantInput[] | undefined,
    callback: (err?: GraphQLError) => void
  ) => {
    try {
      const { data: dataReset, errors: errorsResets } = await client.mutate<
        Mutation,
        MutationResetCartArgs
      >({
        mutation: resetCartMutation(),
        variables: {
          session: { name: sessionId },
        },
        fetchPolicy: "no-cache",
      });

      deleteCachedProducts();
      removeLocalStorageItem("product_insurance");

      const { data: dataChoose, errors: errorsChoose } = await client.mutate<
        Mutation,
        MutationChooseProposalFromKeysArgs
      >({
        mutation: chooseProposalFromKeysMutation(),
        variables: {
          session: { name: sessionId },
          input: {
            proposalKeys,
            occupantList,
          },
        },
        fetchPolicy: "no-cache",
      });
      if (
        dataChoose?.chooseProposalFromKeys &&
        dataChoose.chooseProposalFromKeys.cart
      ) {
        setLocalStorageItem("rsl_proposal_key", JSON.stringify(proposalKeys));
        //Lors de l'ajout d'un séjour au panier, on supprime le localStorage code_fidelite
        removeLocalStorageItem("code_fidelite");

        sendDataLayerAddToCart(dataChoose.chooseProposalFromKeys.cart);
        setCart(dataChoose.chooseProposalFromKeys.cart);
      } else {
        setCart(null);
        removeLocalStorageItem("rsl_proposal_key");
      }
      if (errorsChoose && errorsChoose.length > 0) {
        callback(errorsChoose[0]);
      } else {
        callback();
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        callback(new GraphQLError(error.message));
      }
      setCart(null);
    }
  };

  //#region Proposals
  /**
   * It adds a proposal to the cart.
   * @param {string[]} proposalKeys - The keys of the proposals to add to the cart.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const addProposalFromKeysOld = async (
    proposalKeys: string[],
    occupantList: ChooseProposalFromKeysOccupantInput[] | undefined,
    callback: (err?: GraphQLError, response?: Cart) => void
  ) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationChooseProposalFromKeysArgs>({
          mutation: resetAndChooseProposalFromKeysMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              proposalKeys,
              occupantList,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (
            response.data &&
            response.data.chooseProposalFromKeys &&
            response.data?.chooseProposalFromKeys.cart
          ) {
            setLocalStorageItem(
              "rsl_proposal_key",
              JSON.stringify(proposalKeys)
            );
            setCart(response.data.chooseProposalFromKeys.cart);
            callback(undefined, response.data.chooseProposalFromKeys.cart);
          }
        })
        .catch((err) => {
          callback(err, undefined);
          setCart(null);
        });
    } else {
      const error = new GraphQLError(
        "Invalid session id",
        null,
        null,
        null,
        null,
        null,
        null
      );
      callback(error, undefined);
    }
  };
  //#endregion

  //#region Discount / Gift / Partner Code
  /**
   * It switches the partner convention.
   * @param {string} partnerCode - The partner code of the partner you want to switch to.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const switchToPartnerConvention = (
    partnerCode: string,
    callback: (err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationSwitchToPartnerConventionArgs>({
          mutation: switchToPartnerConventionMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              partnerCode: partnerCode,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.switchToPartnerConvention?.cart) {
            //on clear le cache des préstation si on saisie le code partenaire
            deleteCachedProducts();
            setCart(response.data.switchToPartnerConvention.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          console.log(err);
          callback(err);
        });
    }
  };

  /**
   * Add a discount code to the cart
   * @param {string} discountCode - The discount code to add to the cart.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const addDiscountCode = (
    discountCode: string,
    callback: (err?: GraphQLError) => void
  ): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationAddDiscountCodeArgs>({
          mutation: addDiscountCodeMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              discountCode,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.addDiscountCode?.cart) {
            setCart(response.data.addDiscountCode.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  /**
   * It removes the discount code from the cart.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const removeDiscountCode = (callback: (err?: GraphQLError) => void): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationRemoveDiscountCodeArgs>({
          mutation: removeDiscountCodeMutation(),
          variables: {
            session: { name: sessionId },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.removeDiscountCode?.cart) {
            setCart(response.data.removeDiscountCode.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  /**
   * It adds a gift code to the cart.
   * @param {string} giftCode - The gift code to add to the cart.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const addGiftCode = (
    giftCode: string,
    callback: (err?: GraphQLError) => void
  ): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationAddGiftCodeArgs>({
          mutation: addGiftCodeMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              giftCode: giftCode,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.addGiftCode?.cart) {
            //Lors de l'ajout d'un code fidelité, on inscrit celui-ci dans le localStorage code_fidelite
            setLocalStorageItem("code_fidelite", giftCode);
            setCart(response.data.addGiftCode.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  /**
   * It removes a gift code from the cart.
   * @param {string} giftCode - The gift code to remove from the cart.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const removeGiftCode = (
    giftCode: string,
    callback: (err?: GraphQLError) => void
  ): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationRemoveGiftCodeArgs>({
          mutation: removeGiftCodeMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              giftCode: giftCode,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.removeGiftCode?.cart) {
            //Lors de l'ajout d'un code fidelité, on inscrit celui-ci dans le localStorage code_fidelite
            removeLocalStorageItem("code_fidelite");
            setCart(response.data.removeGiftCode.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };
  //#endregion

  //#region Observation
  /**
   * Add a note to a reservation
   * @param {string} observation - The observation that was made.
   * @param {number[]} reservationCartItemIDs - An array of reservation cart item IDs.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const addObservation = (
    observation: string,
    reservationCartItemIDs: number[],
    callback: (err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationAddObservationArgs>({
          mutation: addObservationMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              observation,
              bookingCartItemIDs: reservationCartItemIDs,
            },
          },
        })
        .then((response) => {
          if (response.data?.addObservation?.cart) {
            setCart(response.data?.addObservation?.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };
  //#endregion

  //#region Occupant
  /**
   * Add an occupant to a stay
   * @param {number} stayGuiid - The stayGuiid of the stay that you want to add to the cart.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const addOccupant = (
    stayGuiid: number,
    callback: (err?: GraphQLError) => void
  ): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationAddOccupantArgs>({
          mutation: addOccupantMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              stayGuiid,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.addOccupant?.cart) {
            setCart(response.data.addOccupant.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  /**
   * It adds a baby to the cart.
   * @param {number} stayGuiid - The id of the stay you want to add a baby to.
   */
  const addBaby = (
    stayGuiid: number,
    callback: (err?: GraphQLError) => void
  ): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationAddBabyArgs>({
          mutation: addBabyMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              stayGuiid,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.addBaby?.cart) {
            setCart(response.data.addBaby.cart);
            callback();
          }
        })
        .catch((err) => {
          //console.log(err);
          callback(err);
        });
    }
  };

  /**
   * Remove an occupant from the cart
   * @param {number} occupantGuiid - The occupant's GUIID.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const removeOccupant = (
    occupantGuiid: number,
    callback: (err?: GraphQLError) => void
  ): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationRemoveOccupantArgs>({
          mutation: removeOccupantMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              occupantGuiid,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.removeOccupant?.cart) {
            setCart(response.data.removeOccupant.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  /**
   * Update the occupant in the cart
   * @param {number} occupantGuiid - The occupant's GUIID.
   * @param {string} firstName - The first name of the occupant.
   * @param {string} lastName - The last name of the occupant.
   * @param {Date} birthDate - Date
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const updateOccupant = (
    occupantGuiid: number,
    firstName: string,
    lastName: string,
    birthDate: Date | null,
    callback: (err?: GraphQLError) => void
  ): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationUpdateOccupantArgs>({
          mutation: updateOccupantMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              occupantGuiid,
              firstName,
              lastName,
              birthDate: birthDate ? dateFormat(birthDate, "yyyy-mm-dd") : null,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.updateOccupant?.cart) {
            setCart(response.data.updateOccupant.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  const confirmFastCheckout = (
    confirmFastCheckout: boolean,
    callback: (err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationConfirmFastCheckoutArgs>({
          mutation: confirmFastCheckoutMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              confirmFastCheckout,
            },
          },
        })
        .then((response) => {
          if (response.data?.confirmFastCheckout?.cart) {
            setCart(response.data.confirmFastCheckout.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  //#endregion

  //#region Payment

  const updatePartnerPaymentsDue = (
    paymentdueType: string,
    callback: (err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationUpdatePartnerPaymentPlansArgs>({
          mutation: updatePartnerPaymentPlansMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              paymentPlan: paymentdueType,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.updatePartnerPaymentPlans?.cart) {
            setCart(response.data.updatePartnerPaymentPlans?.cart);
            callback();
          }
        })
        .catch((err) => {
          callback(err);
        });
    }
  };

  const updatePayerPaymentsDue = (
    paymentdueType: string,
    callback: (err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationUpdatePayerPaymentPlansArgs>({
          mutation: updatePayerPaymentPlansMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              paymentPlan: paymentdueType,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.updatePayerPaymentPlans?.cart) {
            setCart(response.data.updatePayerPaymentPlans.cart);
            callback();
          }
        })
        .catch((err) => {
          callback(err);
        });
    }
  };

  const updateCartPaymentMethod = (
    bookingStatus: BookingStatus,
    ccardType: number,
    paymentMode: string,
    callback: (err?: GraphQLError) => void,
    paymentOptions?: string,
    automaticPayment?: boolean,
    manualPaymentOptions?: string,
    paymentModeSetOnlyFirst?: boolean,
    paymentAmount?: string,
    chequeNumber?: string,
    acceptUrl?: string,
    declineUrl?: string,
    cancelUrl?: string,
    exceptionUrl?: string
  ) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationUpdateCartPaymentMethodArgs>({
          mutation: updateCartPaymentMethodMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              bookingStatus,
              ccardType,
              paymentMode,
              paymentOptions,
              automaticPayment,
              manualPaymentOptions,
              paymentModeSetOnlyFirst,
              paymentAmount,
              chequeNumber,
              url: {
                acceptUrl,
                declineUrl,
                cancelUrl,
                exceptionUrl,
              },
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.updateCartPaymentMethod?.cart) {
            setCart(response.data?.updateCartPaymentMethod.cart);
            callback && callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback && callback(err);
        });
    }
  };

  const updateCartPaymentsDue = (
    paymentdueType: string,
    partnerPaymentdueType: string,
    callback: (err?: GraphQLError) => void
  ): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationUpdateCartPaymentPlansArgs>({
          mutation: updateCartPaymentPlansMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              paymentPlan: paymentdueType,
              partnerPaymentPlan: partnerPaymentdueType,
            },
          },
        })
        .then((response) => {
          if (response.data?.updateCartPaymentPlans?.cart) {
            setCart(response.data?.updateCartPaymentPlans?.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };
  //#endregion

  //#region Product
  /**
   * It adds a product to the global cart.
   * @param {string} productCode - The product code of the product you want to add to the cart.
   * @param {number} productID - The product ID of the product you want to add to the cart.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const addProductToGlobal = (
    productCode: string,
    productID: number,
    quantity: number,
    callback: (err?: GraphQLError) => void,
    stayGuiid?: number
  ): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationAddProductToBookingArgs>({
          mutation: addProductToBookingMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              productCode,
              productID,
              quantity,
              stayGuiid,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.addProductToBooking?.cart) {
            setCart(response.data.addProductToBooking.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  /**
   * Add a product to the cart of an occupant
   * @param {string} productCode - The product code of the product you want to add to the cart.
   * @param {number} productID - The ID of the product you want to add to the cart.
   * @param {number} occupantGuiid - The occupant's GUIID.
   * @param {number} quantity - The quantity of the product to add to the cart.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const addProductToOccupant = (
    productCode: string,
    productID: number,
    occupantGuiid: number,
    quantity: number,
    callback: (err?: GraphQLError) => void
  ): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationAddProductToOccupantArgs>({
          mutation: addProductToOccupantMutation(),
          variables: {
            session: { name: sessionId },
            input: [
              {
                occupantGuiid,
                productCode,
                productID,
                quantity,
              },
            ],
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.addProductToOccupant?.cart) {
            setCart(response.data.addProductToOccupant.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };
  //#endregion

  //#region Cart (clear/save/refresh/deleteOne/removeStay)
  /**
   * It calls the GraphQL API to get the cart for the current session.
   * @param callback - A function that will be called when the query is complete.
   */
  const refresh = (callback: (err?: GraphQLError) => void): void => {
    if (sessionId) {
      client
        .query<Query, QueryGetCartArgs>({
          query: getCartQuery(),
          variables: {
            session: { name: sessionId },
          },
          fetchPolicy: "no-cache",
        })
        .then(async (response) => {
          if (response.data.getCart?.cart) {
            setCart(response.data.getCart.cart);
            if (
              !isEmpty(response.data.getCart.cart?.reservations) &&
              response.data.getCart.cart?.fromFastCheckinCheckout === true
            ) {
              //Si on en mode pré-chechin, on récup la réservzation de base:
              const { data: dataBooking } = await client.query<
                Query,
                QueryGetCustomerBookingsArgs
              >({
                query: getCustomerBookingsQuery(),
                fetchPolicy: "no-cache",
                variables: {
                  session: { name: sessionId },
                  input: {
                    bookingIds: [
                      response.data.getCart.cart?.reservations?.[0]?.id ?? 0,
                    ],
                  },
                },
              });
              const correspondingBooking =
                dataBooking?.getCustomerBookings?.customerBookings?.find(
                  (x) =>
                    x?.id === response.data.getCart?.cart?.reservations?.[0]?.id
                ) ?? null;
              setCustomerBooking(correspondingBooking);
            }
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  const removeStay = (
    stayGuiid: number,
    lastStayRemoveAllFerryConfirmed: boolean,
    callback: (err?: GraphQLError) => void
  ): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationRemoveStayArgs>({
          mutation: removeStayMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              stayGuiid: stayGuiid,
              lastStayRemoveAllFerryConfirmed: lastStayRemoveAllFerryConfirmed,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.removeStay?.cart) {
            setCart(response.data?.removeStay.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          //console.error(err);
          callback(err);
        });
    }
  };

  /**
   * Clear the cart for the current session
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const clear = (
    callback: (err?: GraphQLError) => void,
    updateState: boolean = true
  ): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationResetCartArgs>({
          mutation: resetCartMutation(),
          variables: {
            session: { name: sessionId },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.resetCart?.cart) {
            //on clear le cache des préstation quand on supprime le panier
            deleteCachedProducts();
            //on remove le product assurance séléctionné
            removeLocalStorageItem("product_insurance");

            if (updateState && updateState) {
              setCart(null);
            }
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  /**
   * Save the cart
   * @param {boolean} passNewAmountLowerThanPrevious - If true, the new amount of the cart item must
   * be lower than the previous one.
   * @param {boolean} fastCheckinCheckout - boolean
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const save = (
    passNewAmountLowerThanPrevious: boolean,
    fastCheckinCheckout: boolean,
    callback: (err?: GraphQLError) => void
  ): void => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationSaveCartArgs>({
          mutation: saveCartMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              passNewAmountLowerThanPrevious,
              fastCheckinCheckout,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.saveCart?.cart) {
            //on clear le cache des préstation quand on sauvegarde le panier
            deleteCachedProducts();
            setCart(response.data?.saveCart.cart);
            callback();
            //TODO: Clear le cart une fois le panier sauvegardé ?
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  /**
   * Delete a reservation from the cart
   * @param {number} reservationCartItemID - The ID of the reservation to delete.
   * @param callback - A callback function that is called when the mutation is complete.
   */
  const deleteReservation = (
    reservationCartItemID: number,
    callback: (err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationRemoveBookingFromCartArgs>({
          mutation: removeBookingFromCartMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              bookingCartItemID: reservationCartItemID,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.removeBookingFromCart?.cart) {
            //on clear le cache des préstation quand on supprime une réservation du panier
            deleteCachedProducts();
            setCart(response.data.removeBookingFromCart?.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };

  const addReservationToCart = (
    reservationID: number,
    fastCheckinCheckout: boolean,
    callback: (err?: GraphQLError) => void
  ) => {
    if (sessionId) {
      client
        .mutate<Mutation, MutationUpdateCustomerBookingArgs>({
          mutation: updateCustomerBookingMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              bookingId: reservationID,
              fastCheckinCheckout: fastCheckinCheckout,
            },
          },
        })
        .then((response) => {
          if (response.data?.updateCustomerBooking?.cart) {
            deleteCachedProducts();
            removeLocalStorageItem("product_insurance");
            setCart(response.data?.updateCustomerBooking.cart);
            callback();
          }
        })
        .catch((err: GraphQLError) => {
          callback(err);
        });
    }
  };
  //#endregion

  return (
    <CartContext.Provider
      value={{
        cart,
        customerBooking,
        setCart,
        setCustomerBooking,
        loading,
        refresh,
        addProposalFromKeys,
        removeStay,
        addProductToGlobal,
        addProductToOccupant,
        removeOccupant,
        addOccupant,
        addBaby,
        updateOccupant,
        addDiscountCode,
        addGiftCode,
        removeDiscountCode,
        removeGiftCode,
        confirmFastCheckout,
        addObservation,
        clear,
        save,
        switchToPartnerConvention,
        deleteReservation,
        updatePartnerPaymentsDue,
        updatePayerPaymentsDue,
        updateCartPaymentMethod,
        updateCartPaymentsDue,
        addReservationToCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
